import { useReducer, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { accountListsFileUpload } from '@global/api'
import {
  useListUploadStatusQuery,
  uploadStatus,
  KEY as UPLOAD_LIST_QUERY_KEY,
} from '@global/hooks/queries/useListsUploadStatusQuery'
import { importStatus } from './types'

const useUploadStatus = ({ listId, status }) => {
  const queryClient = useQueryClient()
  const [checkUploadStatus, setCheckUploadStatus] = useState(true)

  // Run the query if the status...
  // - Is in progress (to check the upload status)
  // - Has not been run yet;
  const enabled =
    typeof listId !== 'undefined' && (importStatus.IN_PROGRESS === status || checkUploadStatus)

  const { data: listUploadStatusResponse } = useListUploadStatusQuery({
    listId,
    enabled,
    refetchInterval: enabled ? 2000 : undefined,
    onSuccess: response => {
      const check = response?.status === uploadStatus.PROCESSING
      setCheckUploadStatus(check)
    },
  })

  // Failed but still uploading indicates a stale query.
  if (listUploadStatusResponse?.status === uploadStatus.FAIL && status === importStatus.UPLOADING) {
    queryClient.removeQueries({ queryKey: [UPLOAD_LIST_QUERY_KEY, listId], exact: true })
  }

  return {
    listUploadStatusResponse,
  }
}

interface IState {
  uploadFile: File | null
  status: importStatus
  progress: number
  error: {
    message: string
    invalidRows?: number[]
  }
}

export const UploadActionType = {
  SUCCESSFUL_UPLOAD: 'SUCCESSFUL_UPLOAD',
  FAILED_UPLOAD: 'FAILED_UPLOAD',
  PROCESSING_IN_PROGRESS: 'PROCESSING_IN_PROGRESS',
  RESET: 'RESET_TO_DEFAULT_STATUS',
  SET_PROGRESS: 'SET_PROGRESS',
} as const

export type UploadActionType = (typeof UploadActionType)[keyof typeof UploadActionType]

const DEFAULT_ERROR_MESSAGE = 'Something went wrong uploading the list.'

type Action =
  | { type: typeof UploadActionType.SUCCESSFUL_UPLOAD }
  | {
      type: typeof UploadActionType.FAILED_UPLOAD
      payload: { message: string; invalidRows?: number[] }
    }
  | { type: typeof UploadActionType.PROCESSING_IN_PROGRESS }
  | { type: typeof UploadActionType.RESET }
  | { type: typeof UploadActionType.SET_PROGRESS; payload: { tick: number; status?: importStatus } }

const reducer = (state: IState, action: Action) => {
  switch (action.type) {
    case UploadActionType.SUCCESSFUL_UPLOAD:
      return {
        ...state,
        status: importStatus.IN_PROGRESS,
        uploadFile: null,
      }
    case UploadActionType.FAILED_UPLOAD:
      return {
        ...state,
        error: action.payload,
        status: importStatus.FAILED,
      }
    case UploadActionType.PROCESSING_IN_PROGRESS:
      return {
        ...state,
        status: importStatus.IN_PROGRESS,
      }
    case 'SET_PROGRESS': {
      const { tick, status = importStatus.UPLOADING } = action.payload
      return {
        ...state,
        status,
        progress: tick,
      }
    }
    case UploadActionType.RESET:
      return { ...state, uploadFile: null, status: importStatus.DEFAULT, progress: 0 }
    default:
      return state
  }
}

const initialState = {
  uploadFile: null,
  status: importStatus.DEFAULT,
  error: {
    message: '',
    invalidRows: [],
  },
  progress: 0,
}

interface IUseFileUpload {
  defaultStatus?: importStatus
  listId: number | undefined
  setReadOnlyCallback: (readOnly: boolean) => void
}

/**
 * Provides functionality for file upload management within a component, including upload progress tracking, and status management.
 * It encapsulates the logic for uploading a file, tracking its upload progress, and managing the upload status.
 * The hook leverages `useMutation` for performing the upload operation and `useQueryClient` for cache invalidation.
 *
 * @param {Object} params - The parameters for configuring the file upload.
 * @param {importStatus} [params.defaultStatus=importStatus.DEFAULT] - The initial status of the file import.
 * @param {Function} [params.onSuccess] - Callback function to be called upon successful upload.
 * @param {string} params.listId - The ID of the list associated with the file upload.
 * @param {Function} params.setReadOnlyCallback - Callback function to set the read-only state of the UI based on the upload status.
 * @returns {Object} An object containing methods and states related to file upload.
 *         This includes the `uploadFile` state for storing the current file to be uploaded,
 *         `setUploadFile` method to update the `uploadFile`,
 *         `upload` function to initiate the file upload process,
 *         `uploadError` for any errors encountered during upload,
 *         `status` and `setStatus` for managing the current status of the upload,
 *         `progress` and `setProgress` for tracking the upload progress,
 *         and `resetToDefaultStatus` method to reset the upload status, file, and progress to their default states.
 *
 * The hook also internally manages the upload status based on the current list upload status,
 * automatically setting it to `FAILED`, `SUCCESS`, or `IN_PROGRESS` as appropriate.
 * It ensures that the UI and query caches are updated or invalidated accordingly to reflect the latest upload status.
 */
export const useFileUpload = ({ defaultStatus, listId, setReadOnlyCallback }: IUseFileUpload) => {
  const queryClient = useQueryClient()
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    status: defaultStatus ?? importStatus.DEFAULT,
  })
  const { uploadFile, progress, status, error } = state
  const { listUploadStatusResponse } = useUploadStatus({ listId, status })
  const listUploadStatus = listUploadStatusResponse?.status
  const inProgress = status === importStatus.IN_PROGRESS

  const { mutate: upload, error: uploadError } = useMutation(accountListsFileUpload, {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['files'] })
      dispatch({ type: UploadActionType.SUCCESSFUL_UPLOAD })
    },
    onError: () => {
      dispatch({
        type: UploadActionType.FAILED_UPLOAD,
        payload: { message: DEFAULT_ERROR_MESSAGE },
      })
    },
  })

  // If the list upload status is in a failed state after processing, set the status to `FAILED`.
  if (listUploadStatus === uploadStatus.FAIL && inProgress) {
    const invalidRows = JSON.parse(listUploadStatusResponse?.invalid_rows || '[]')
    const errorMessage = listUploadStatusResponse?.error_message || DEFAULT_ERROR_MESSAGE

    dispatch({
      type: UploadActionType.FAILED_UPLOAD,
      payload: { message: errorMessage, invalidRows },
    })
    resetListUploadStatusQuery()
  }

  // If the list upload status is in a success state after processing, set the status to `SUCCESS`.
  if (listUploadStatus === uploadStatus.SUCCESS && inProgress) {
    // TODO: Refactor list API query to use React Query so we can invalidate its cache here,
    // instead of using `window.location.reload()`.
    // resetToDefaultStatus()
    window.location.reload()
  }

  // If the list upload status is in a processing state after processing, set the status to `IN_PROGRESS`.
  // This scenario is for the initial page load, when the list upload status is still processing.
  if (listUploadStatus === uploadStatus.PROCESSING && !inProgress) {
    setReadOnlyCallback(true)
    dispatch({ type: UploadActionType.PROCESSING_IN_PROGRESS })
  }

  return {
    uploadFile,
    upload,
    uploadError,
    status,
    error,
    progress,
    resetToDefaultStatus,
    dispatchFileUpload: dispatch,
  }

  // Reset the list upload status query.
  // This is necessary to ensure that the list upload status is updated after a failed upload;
  // Otherwise, the status will remain in the `FAILED` state or the last cached value, even if the
  // query cache is invalidated.
  function resetListUploadStatusQuery() {
    queryClient.removeQueries({ queryKey: [UPLOAD_LIST_QUERY_KEY, listId], exact: true })
  }

  function resetToDefaultStatus() {
    dispatch({ type: UploadActionType.RESET })
    resetListUploadStatusQuery()
    setReadOnlyCallback(false)
  }
}
